/**
 * Country dial-code catalogue for {@link PhoneInput}. Kept small and curated
 * (North America first for the beta, then common LATAM / EU / others) rather
 * than a full ITU list — enough to cover Yaruta's audience while staying a
 * readable data file. Extend by adding rows; the array order is also the
 * tie-break priority when two countries share a dial code (e.g. +1 → US wins
 * over CA on parse), so keep the preferred default first within each code.
 */
export interface Country {
  /** ISO 3166-1 alpha-2, uppercase — also drives the flag emoji. */
  iso: string;
  name: string;
  /** E.164 country calling code, with leading '+', e.g. '+52'. */
  dial: string;
}

// US / MX / CA first: the beta's primary audience and the +1 tie-break order.
export const COUNTRIES: readonly Country[] = [
  { iso: 'US', name: 'United States', dial: '+1' },
  { iso: 'MX', name: 'Mexico', dial: '+52' },
  { iso: 'CA', name: 'Canada', dial: '+1' },
  { iso: 'AR', name: 'Argentina', dial: '+54' },
  { iso: 'AU', name: 'Australia', dial: '+61' },
  { iso: 'BE', name: 'Belgium', dial: '+32' },
  { iso: 'BO', name: 'Bolivia', dial: '+591' },
  { iso: 'BR', name: 'Brazil', dial: '+55' },
  { iso: 'CL', name: 'Chile', dial: '+56' },
  { iso: 'CO', name: 'Colombia', dial: '+57' },
  { iso: 'CR', name: 'Costa Rica', dial: '+506' },
  { iso: 'DK', name: 'Denmark', dial: '+45' },
  { iso: 'DO', name: 'Dominican Republic', dial: '+1' },
  { iso: 'EC', name: 'Ecuador', dial: '+593' },
  { iso: 'SV', name: 'El Salvador', dial: '+503' },
  { iso: 'FR', name: 'France', dial: '+33' },
  { iso: 'DE', name: 'Germany', dial: '+49' },
  { iso: 'GT', name: 'Guatemala', dial: '+502' },
  { iso: 'HN', name: 'Honduras', dial: '+504' },
  { iso: 'IN', name: 'India', dial: '+91' },
  { iso: 'IE', name: 'Ireland', dial: '+353' },
  { iso: 'IL', name: 'Israel', dial: '+972' },
  { iso: 'IT', name: 'Italy', dial: '+39' },
  { iso: 'JP', name: 'Japan', dial: '+81' },
  { iso: 'NL', name: 'Netherlands', dial: '+31' },
  { iso: 'NZ', name: 'New Zealand', dial: '+64' },
  { iso: 'NI', name: 'Nicaragua', dial: '+505' },
  { iso: 'NO', name: 'Norway', dial: '+47' },
  { iso: 'PA', name: 'Panama', dial: '+507' },
  { iso: 'PY', name: 'Paraguay', dial: '+595' },
  { iso: 'PE', name: 'Peru', dial: '+51' },
  { iso: 'PH', name: 'Philippines', dial: '+63' },
  { iso: 'PL', name: 'Poland', dial: '+48' },
  { iso: 'PT', name: 'Portugal', dial: '+351' },
  { iso: 'PR', name: 'Puerto Rico', dial: '+1' },
  { iso: 'SG', name: 'Singapore', dial: '+65' },
  { iso: 'ZA', name: 'South Africa', dial: '+27' },
  { iso: 'KR', name: 'South Korea', dial: '+82' },
  { iso: 'ES', name: 'Spain', dial: '+34' },
  { iso: 'SE', name: 'Sweden', dial: '+46' },
  { iso: 'CH', name: 'Switzerland', dial: '+41' },
  { iso: 'GB', name: 'United Kingdom', dial: '+44' },
  { iso: 'UY', name: 'Uruguay', dial: '+598' },
  { iso: 'VE', name: 'Venezuela', dial: '+58' },
];

export const DEFAULT_ISO = 'US';

/** Longest dial code in the catalogue (digits), used to bound prefix matching. */
const MAX_DIAL_DIGITS = 4;

/** Strip everything but digits. */
function digitsOnly(value: string): string {
  return value.replace(/\D/g, '');
}

export function countryByIso(iso: string): Country {
  return (
    COUNTRIES.find((c) => c.iso === iso) ??
    COUNTRIES.find((c) => c.iso === DEFAULT_ISO)!
  );
}

/** Regional-indicator flag emoji for a 2-letter ISO code, e.g. 'US' → 🇺🇸. */
export function isoToFlag(iso: string): string {
  return iso
    .toUpperCase()
    .replace(/[A-Z]/g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));
}

export interface ParsedPhone {
  /**
   * ISO of the country whose dial code matched, or `null` when none did.
   *
   * `null` is not "unknown, assume the default" — it is a distinct outcome the
   * caller has to honour. It used to be {@link DEFAULT_ISO}, and because the
   * caller cannot tell a real US match from a fallback, every number from a
   * country outside the catalogue was re-anchored to +1: pasting `+998901234567`
   * (Uzbekistan) emitted `+1998901234567`. That is silent corruption of
   * something the user typed correctly — it passes validation, gets stored, and
   * fails at Twilio much later, or reaches a real US number (YAR-410).
   */
  iso: string | null;
  /**
   * National significant number (digits only, no country code).
   *
   * When {@link ParsedPhone.iso} is `null` there is no country code to strip,
   * so this holds every digit of the number.
   */
  national: string;
}

/**
 * Split a full/partial E.164 string into a country + national number. Matches
 * the LONGEST dial-code prefix so multi-digit codes (e.g. +506) win over their
 * shorter cousins; ties fall to catalogue order (so +1 → US, not CA/DO).
 *
 * Returns `iso: null` with every digit as the national part when no code
 * matches, so {@link composeE164} round-trips the number verbatim instead of
 * inventing a country for it. The catalogue is a convenience for the picker,
 * not the set of numbers this product accepts: Twilio reaches roughly 180
 * countries and the catalogue carries 45.
 */
export function parseE164(raw: string): ParsedPhone {
  const digits = digitsOnly(raw);
  for (let len = Math.min(MAX_DIAL_DIGITS, digits.length); len >= 1; len--) {
    const code = digits.slice(0, len);
    const match = COUNTRIES.find((c) => c.dial.slice(1) === code);
    if (match) return { iso: match.iso, national: digits.slice(len) };
  }
  return { iso: null, national: digits };
}

/**
 * Compose an E.164 string from a country + national number. Returns '' when the
 * national part is empty so an untouched field reads as "no number" (keeping the
 * caller's existing empty/invalid handling intact) rather than a bare dial code.
 *
 * A `null` country is passed through as `+` plus the digits it was given. That
 * is what makes the round-trip lossless for a country the catalogue does not
 * carry, and it is the whole fix for YAR-410: the number leaves exactly as the
 * user typed it.
 */
export function composeE164(iso: string | null, national: string): string {
  const nat = digitsOnly(national);
  if (!nat) return '';
  if (iso === null) return `+${nat}`;
  return countryByIso(iso).dial + nat;
}
